import { FC, useState, useRef, useEffect } from "react";
import { DateRange, RangeKeyDict } from "react-date-range";
import "react-date-range/dist/styles.css"; // Estilos base para el DateRangePicker
import "react-date-range/dist/theme/default.css"; // Tema por defecto
import { es } from "date-fns/locale/es";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type DateRangePickerProps = {
  onDateChange?: (startDate: Date, endDate: Date) => void;
  id: string;
  label: string;
  textInformation?: string; // Agregamos el texto para el tooltip
};

const DateRangePickerIconInformation: FC<DateRangePickerProps> = ({
  onDateChange,
  id,
  label,
  textInformation,
}) => {
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("es-ES").replace(/\//g, "-");
  };

  const handleSelect = (ranges: RangeKeyDict) => {
    const { selection } = ranges;
    setState([selection]);
    if (onDateChange) {
      onDateChange(selection.startDate, selection.endDate);
    }
  };

  const toggleDatePicker = () => {
    setIsPickerVisible((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsPickerVisible(false);
      }
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const inputField = document.getElementById(id);
    if (inputField) {
      const rect = inputField.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (windowHeight - rect.bottom < 300) {
        setIsAbove(true);
      } else {
        setIsAbove(false);
      }
    }
  }, [isPickerVisible]);

  const [isAbove, setIsAbove] = useState(false);

  return (
    <div className="relative">
      <div className="flex items-center">
        <label
          htmlFor={id}
          className="-mt-1 block text-sm font-medium leading-6 text-gray-900"
        >
          {label}
        </label>

        <div className="relative ml-2">
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="text-gray-500 cursor-pointer"
            onClick={() => setIsTooltipVisible((prev) => !prev)}
          />

          {isTooltipVisible && (
            <div
              ref={tooltipRef}
              className="absolute left-0 top-full mt-1 w-40 p-2 text-gray-500 bg-white text-xs rounded-md shadow-lg opacity-100 transition-opacity"
            >
              {textInformation}
            </div>
          )}
        </div>
      </div>

      <input
        id={id}
        type="text"
        value={`${formatDate(state[0].startDate)} - ${formatDate(
          state[0].endDate
        )}`}
        readOnly
        className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-400 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        onClick={toggleDatePicker}
        placeholder="Select date range"
      />

      <div
        ref={datePickerRef}
        className={`absolute z-50 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg ${
          isAbove ? "bottom-full mb-2" : "top-full mt-2"
        } ${isPickerVisible ? "" : "hidden"}`}
      >
        <DateRange
          editableDateInputs={true}
          onChange={handleSelect}
          moveRangeOnFirstSelection={false}
          ranges={state}
          className="date-range-picker"
          locale={es}
        />
      </div>
    </div>
  );
};

export default DateRangePickerIconInformation;
